/**
 * GG-10 title templates. One builder for every page type, so the drop rules
 * live in one place: https://linear.app/dupecom/issue/GG-10
 *
 * A title is a name part (never cut; it is also the page's H1) followed by
 * ordered parts. Each part carries its own leading separator (": ", " ", " | ")
 * so dropping one never leaves a dangling colon or double space. Parts with a
 * `dropRank` are removed lowest rank first until the title fits; parts without
 * one stay unless even the fully-dropped title is too long, in which case the
 * name is returned alone.
 */

export const TITLE_MAX_LENGTH = 60
export const SITE_NAME_SUFFIX = ' | Dupe.com'
/** `{N}` is only shown at this count or above. */
export const MIN_COUNT_TO_SHOW = 5

export type TitlePart = {
  /** null/undefined/false/'' = the value is missing; the part drops with its words. */
  text: string | null | undefined | false
  /** Lower drops first when over the limit. Omit for parts that only go with the name-only fallback. */
  dropRank?: number
}

export type TitleTemplateInput = {
  name: string
  parts?: TitlePart[]
  maxLength?: number
}

export type BuiltTitle = {
  title: string
  /** The name part, for the H1. */
  name: string
}

// A part that still holds an unfilled slot or a bad value is treated as
// missing rather than shipped: never "{N}", "undefined", "NaN" or "$0".
// Only leftovers that can't be real words: braces, $0, and the lowercase
// "undefined"/"null"/"NaN" a template literal writes for a missing value.
// "Infinity Scarves" is a real category, so Infinity is not on the list.
const BROKEN_PART = /[{}]|\b(?:undefined|null|NaN)\b|\$0(?![.,]?\d*[1-9])/

const SEPARATOR_ONLY = /^[\s:|,\u2013\u2014-]+$/

// Visible characters, not UTF-16 code units, so an emoji counts once.
const charLength = (text: string) => Array.from(text).length

const LEADING_SEPARATOR = /^\s*(?:[:|,\u2013\u2014]|-(?:\s|$))/

const isUsable = (text: TitlePart['text']): text is string =>
  typeof text === 'string' && text.trim() !== '' && !BROKEN_PART.test(text)

export function siteNamePart(dropRank?: number): TitlePart {
  return { text: SITE_NAME_SUFFIX, dropRank }
}

/** "1,240" for 5 or more, else null so the count drops with its words. */
export function formatTitleCount(
  count: number | null | undefined
): string | null {
  if (typeof count !== 'number' || !Number.isFinite(count)) return null
  const whole = Math.floor(count)
  if (whole < MIN_COUNT_TO_SHOW) return null
  return whole.toLocaleString('en-US')
}

/** Lowest live price rounded up to a whole dollar ("$149"), or null when unknown or not positive. */
export function formatTitleMinPrice(
  price: number | null | undefined
): string | null {
  if (typeof price !== 'number' || !Number.isFinite(price) || price <= 0)
    return null
  return `$${Math.ceil(price).toLocaleString('en-US')}`
}

export function buildTitle({
  name,
  parts = [],
  maxLength = TITLE_MAX_LENGTH,
}: TitleTemplateInput): BuiltTitle {
  const cleanName = name.trim()
  let kept = parts
    .map((part, index) => ({ ...part, index }))
    .filter((part): part is typeof part & { text: string } =>
      isUsable(part.text)
    )

  // A punctuation-only part (": ") is kept only when the next kept part is
  // real content: not the site name and not another separator, so "Name:",
  // "Name: | Dupe.com" and "Name: | 2026" can't happen.
  const render = () =>
    cleanName +
    kept
      .filter((part, i) => {
        if (!SEPARATOR_ONLY.test(part.text)) return true
        const next = kept[i + 1]
        return (
          !!next &&
          next.text !== SITE_NAME_SUFFIX &&
          !LEADING_SEPARATOR.test(next.text) &&
          kept
            .slice(i + 1)
            .some(
              (later) =>
                !SEPARATOR_ONLY.test(later.text) &&
                later.text !== SITE_NAME_SUFFIX
            )
        )
      })
      .map((part) => part.text)
      .join('')

  const dropOrder = kept
    .filter((part) => typeof part.dropRank === 'number')
    .sort(
      (a, b) =>
        (a.dropRank as number) - (b.dropRank as number) || b.index - a.index
    )

  for (const part of dropOrder) {
    if (charLength(render()) <= maxLength) break
    kept = kept.filter((k) => k.index !== part.index)
  }

  const title = charLength(render()) <= maxLength ? render() : cleanName
  return { title, name: cleanName }
}
